import { useEffect, useState } from 'react';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { ConfirmDialog } from '../../job-roles/components/ConfirmDialog';
import { AccountDialog, ImportDialog } from '../components/ChartAccountDialogs';
import { exportErrorMessage, isForbidden } from '../exportErrors';
import { CHART_ACCOUNT_TYPES, type ChartAccount, type ChartAccountType, type Paged } from '../exportTypes';
import { accountingGateway } from '../index';
import { useExportCapabilities } from '../useExportCapabilities';
import '../../job-roles/job-roles.css';
import '../accounting-export.css';

type LoadState = { kind: 'loading' } | { kind: 'ready'; data: Paged<ChartAccount> } | { kind: 'forbidden' } | { kind: 'error'; message: string };
const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

export function ChartOfAccountsTab() {
  const { caps } = useExportCapabilities();
  const canManage = caps?.canManageChartOfAccounts !== false;
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [type, setType] = useState<ChartAccountType | ''>('');
  const [status, setStatus] = useState<'' | 'active' | 'archived'>('');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<{ kind: 'add' } | { kind: 'edit'; account: ChartAccount } | { kind: 'import' } | null>(null);
  const [archiving, setArchiving] = useState<ChartAccount | null>(null);
  const [busy, setBusy] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    accountingGateway
      .listChartAccounts({
        ...(debounced ? { search: debounced } : {}), ...(type ? { type } : {}), ...(status ? { active: status === 'active' } : {}), page, pageSize: PAGE_SIZE,
      }, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setState({ kind: 'ready', data }); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: exportErrorMessage(err, 'Could not load the chart of accounts.') });
      });
    return () => controller.abort();
  }, [debounced, type, status, page, reloadNonce]);

  async function restore(a: ChartAccount) {
    setNotice(null);
    try {
      await accountingGateway.restoreChartAccount(a.id);
      setNotice(`Restored account ${a.accountNumber}. It can be used in new mappings again.`);
      setReloadNonce((n) => n + 1);
    } catch (err) {
      setNotice(exportErrorMessage(err, 'The account could not be restored.'));
    }
  }

  async function archive() {
    if (!archiving) return;
    setBusy(true);
    setArchiveError(null);
    try {
      await accountingGateway.archiveChartAccount(archiving.id);
      setNotice(`Archived account ${archiving.accountNumber}. It cannot be used in new mappings; existing exports are unchanged.`);
      setArchiving(null);
      setReloadNonce((n) => n + 1);
    } catch (err) {
      setArchiveError(exportErrorMessage(err, 'The account could not be archived.'));
    } finally {
      setBusy(false);
    }
  }

  if ((caps && !caps.canPreview) || state.kind === 'forbidden') {
    return (
      <section className="ax-tab" aria-labelledby="ax-coa-title">
        <h3 id="ax-coa-title">Chart of Accounts</h3>
        <AccessDeniedNotice requiredPermissionLabel="permission to view the chart of accounts" />
      </section>
    );
  }

  const data = state.kind === 'ready' ? state.data : null;
  const totalPages = data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1;

  return (
    <section className="ax-tab" aria-labelledby="ax-coa-title">
      <h3 id="ax-coa-title">Chart of Accounts</h3>
      <p className="muted ax-lead">
        The general ledger accounts payroll amounts can be mapped to. Add accounts one at a time or import them from a CSV file.
        An archived account cannot be used in new mappings, and exports that already used it keep their own copy of the account number and name.
      </p>
      {notice && <p className="jr-notice jr-notice--ok" role="status">{notice}</p>}
      {caps && !caps.canManageChartOfAccounts && <p className="jr-readonly" role="note">You can view the chart of accounts, but changing it needs a permission you do not have.</p>}

      <div className="ax-toolbar" role="search" aria-label="Filter accounts">
        <div className="jr-field jr-field--search">
          <label htmlFor="ax-coa-search">Search by number or name</label>
          <input id="ax-coa-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="jr-field">
          <label htmlFor="ax-coa-type">Account type</label>
          <select id="ax-coa-type" value={type} onChange={(e) => { setType(e.target.value as ChartAccountType | ''); setPage(1); }}>
            <option value="">All types</option>
            {CHART_ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="jr-field">
          <label htmlFor="ax-coa-status">Status</label>
          <select id="ax-coa-status" value={status} onChange={(e) => { setStatus(e.target.value as '' | 'active' | 'archived'); setPage(1); }}>
            <option value="">Active and archived</option>
            <option value="active">Active only</option>
            <option value="archived">Archived only</option>
          </select>
        </div>
        <span className="ax-toolbar__spacer" />
        {canManage && (
          <div className="jr-actions">
            <button type="button" className="jr-btn jr-btn--secondary" onClick={() => setDialog({ kind: 'import' })}>Import CSV</button>
            <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog({ kind: 'add' })}>Add account</button>
          </div>
        )}
      </div>

      {state.kind === 'loading' && <p role="status" className="muted">Loading accounts…</p>}
      {state.kind === 'error' && <RetryableError message={state.message} onRetry={() => { setState({ kind: 'loading' }); setReloadNonce((n) => n + 1); }} />}

      {data && data.items.length === 0 && (
        <p className="muted" data-testid="no-accounts">
          {debounced || type || status ? 'No accounts match these filters.' : 'No accounts yet. Add an account or import a CSV file to get started.'}
        </p>
      )}

      {data && data.items.length > 0 && (
        <div className="jr-table-scroll" role="region" aria-label="Accounts table" tabIndex={0}>
          <table className="jr-table jr-table--actions ax-table-wide" data-testid="accounts-table">
            <caption className="ax-caption">Chart of accounts ({data.totalCount})</caption>
            <thead>
              <tr>
                <th scope="col">Account number</th>
                <th scope="col">Name</th>
                <th scope="col">Type</th>
                <th scope="col">Status</th>
                <th scope="col">Source</th>
                <th scope="col">External reference</th>
                <th scope="col" className="num">Used in mappings</th>
                {canManage && <th scope="col">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {data.items.map((a) => (
                <tr key={a.id}>
                  <th scope="row">{a.accountNumber}</th>
                  <td>{a.name}</td>
                  <td>{a.accountType}</td>
                  <td>{a.isActive ? <span className="ax-badge ax-badge--ok"><span aria-hidden="true">✔</span> Active</span> : <span className="ax-badge ax-badge--neutral"><span aria-hidden="true">–</span> Archived</span>}</td>
                  <td>{a.source === 'CsvImport' ? 'CSV import' : 'Added by hand'}</td>
                  <td>{a.externalReference ?? ''}</td>
                  <td className="num">{a.mappingUseCount}</td>
                  {canManage && (
                    <td className="ax-row-actions">
                      <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setDialog({ kind: 'edit', account: a })} aria-label={`Edit account ${a.accountNumber}`}>Edit</button>
                      {a.isActive ? (
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => { setArchiving(a); setArchiveError(null); }} aria-label={`Archive account ${a.accountNumber}`}>Archive</button>
                      ) : (
                        <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => void restore(a)} aria-label={`Restore account ${a.accountNumber}`}>Restore</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.totalCount > data.pageSize && (
        <nav className="jr-pager" aria-label="Pagination">
          <span>Page {data.page} of {totalPages}</span>
          <span className="jr-pager__nav">
            <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1}>Previous</button>
            <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={data.page >= totalPages}>Next</button>
          </span>
        </nav>
      )}

      {dialog?.kind === 'add' && (
        <AccountDialog
          onClose={() => setDialog(null)}
          onSaved={(saved) => { setDialog(null); setNotice(`Added account ${saved.accountNumber}: ${saved.name}.`); setReloadNonce((n) => n + 1); }}
        />
      )}
      {dialog?.kind === 'edit' && (
        <AccountDialog
          account={dialog.account}
          onClose={() => setDialog(null)}
          onSaved={(saved) => { setDialog(null); setNotice(`Saved account ${saved.accountNumber}.`); setReloadNonce((n) => n + 1); }}
        />
      )}
      {dialog?.kind === 'import' && (
        <ImportDialog
          onClose={() => setDialog(null)}
          onImported={(n) => { setDialog(null); setNotice(`Imported ${n} account${n === 1 ? '' : 's'}.`); setPage(1); setReloadNonce((x) => x + 1); }}
        />
      )}
      {archiving && (
        <ConfirmDialog title={`Archive account ${archiving.accountNumber}?`} confirmLabel="Archive account" onConfirm={() => void archive()} onCancel={() => setArchiving(null)} busy={busy} error={archiveError}>
          <p>
            {archiving.name} will no longer be offered when you add a mapping. It is used by {archiving.mappingUseCount} active mapping{archiving.mappingUseCount === 1 ? '' : 's'}
            {archiving.mappingUseCount > 0 ? ', which will block a payroll export until you map those items to another account' : ''}.
            Exports that were already generated do not change, and you can restore the account later.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
